import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { extractErrorMessage } from '@/pages/painel-pedidos/lib/errors';
import {
  TIPO_LABELS,
  SOLUCOES_LABELS,
  STATUS_LABELS,
  STATUS_FLOW,
  FONTE_TIPOS,
  FONTE_TIPO_LABELS,
  PAPEL_FONTE_LABELS,
  REGIOES,
  CONFIANCA_LABELS,
  type FonteVinculo,
  type RadarOportunidade,
} from '@/pages/painel/lib/radarConstants';

interface Props {
  op: RadarOportunidade | null;
  password: string;
  onClose: () => void;
  onSaved: () => void;
}

interface FonteSel {
  fonte_id: number | null;
  fonte_nome: string;
  fonte_tipo: string;
  papel: string;
  url_especifica: string;
  observacao: string;
}

interface CatalogoItem {
  id: number;
  nome: string;
  tipo: string;
  status?: string;
}

function num(v: string): number | null {
  if (v.trim() === '') return null;
  const n = Number(v);
  return Number.isNaN(n) ? null : n;
}

function dt(v: string): string | null {
  return v.trim() === '' ? null : v;
}

export default function RadarOportunidadeFormModal({ op, password, onClose, onSaved }: Props) {
  const [titulo, setTitulo] = useState(op?.titulo ?? '');
  const [empresa, setEmpresa] = useState(op?.empresa ?? '');
  const [tipo, setTipo] = useState(op?.tipo ?? '');
  const [segmento, setSegmento] = useState(op?.segmento ?? '');
  const [cidade, setCidade] = useState(op?.cidade ?? '');
  const [uf, setUf] = useState(op?.uf ?? '');
  const [regiao, setRegiao] = useState(op?.regiao ?? '');
  const [dataDescoberta, setDataDescoberta] = useState(op?.data_descoberta ?? '');
  const [estagioProjeto, setEstagioProjeto] = useState(op?.estagio_projeto ?? '');
  const [previsaoExecucao, setPrevisaoExecucao] = useState(op?.previsao_execucao ?? '');
  const [previsaoCompra, setPrevisaoCompra] = useState(op?.previsao_compra ?? '');
  const [valorObra, setValorObra] = useState(op?.valor_obra != null ? String(op.valor_obra) : '');
  const [valorContratoCau, setValorContratoCau] = useState(op?.valor_contrato_cau != null ? String(op.valor_contrato_cau) : '');
  const [valorHop, setValorHop] = useState(op?.valor_potencial_hop != null ? String(op.valor_potencial_hop) : '');
  const [custo, setCusto] = useState(op?.custo_estimado != null ? String(op.custo_estimado) : '');
  const [lucro, setLucro] = useState(op?.lucro_potencial != null ? String(op.lucro_potencial) : '');
  const [margem, setMargem] = useState(op?.margem_estimada != null ? String(op.margem_estimada) : '');
  const [solucoes, setSolucoes] = useState<string[]>(op?.solucoes ?? []);
  const [confianca, setConfianca] = useState(op?.confianca ?? '');
  const [responsavel, setResponsavel] = useState(op?.responsavel ?? '');
  const [proximaAcao, setProximaAcao] = useState(op?.proxima_acao ?? '');
  const [proximaAcaoEm, setProximaAcaoEm] = useState(op?.proxima_acao_em ?? '');
  const [status, setStatus] = useState(op?.status ?? 'detectada');
  const [descricao, setDescricao] = useState(op?.descricao ?? '');
  const [observacoes, setObservacoes] = useState(op?.observacoes ?? '');

  // Fontes (relação muitos-para-muitos).
  const [fontesSel, setFontesSel] = useState<FonteSel[]>([]);
  const [catalogo, setCatalogo] = useState<CatalogoItem[]>([]);
  const [fonteAddOpen, setFonteAddOpen] = useState(false);
  const [fonteAddModo, setFonteAddModo] = useState<'existente' | 'nova'>('existente');
  const [fonteAddId, setFonteAddId] = useState<number | ''>('');
  const [fonteAddNome, setFonteAddNome] = useState('');
  const [fonteAddTipo, setFonteAddTipo] = useState('manual');
  const [fonteAddPapel, setFonteAddPapel] = useState('referencia');
  const [fonteAddUrl, setFonteAddUrl] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [catRes, fontesRes] = await Promise.all([
          supabase.functions.invoke('radar-admin', { body: { action: 'fontes_catalogo', password } }),
          op?.id
            ? supabase.functions.invoke('radar-admin', { body: { action: 'oportunidade_fontes', password, oportunidade_id: op.id } })
            : Promise.resolve({ data: null, error: null }),
        ]);
        const catParsed = catRes.data as { fontes?: CatalogoItem[]; error?: string };
        if (!catParsed?.error) setCatalogo(catParsed?.fontes ?? []);

        if (fontesRes?.data) {
          const fp = fontesRes.data as { fontes?: FonteVinculo[]; error?: string };
          if (!fp?.error && Array.isArray(fp?.fontes)) {
            setFontesSel(fp.fontes.map((f) => ({
              fonte_id: f.fonte_id,
              fonte_nome: f.fonte_nome ?? '',
              fonte_tipo: f.fonte_tipo ?? '',
              papel: f.papel,
              url_especifica: f.url_especifica ?? '',
              observacao: f.observacao ?? '',
            })));
          }
        }
      } catch {
        // silencioso — o usuário ainda pode adicionar manualmente
      }
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSolucao = (s: string) => {
    setSolucoes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const marcarPrincipal = (index: number) => {
    setFontesSel((prev) =>
      prev.map((f, i) => ({ ...f, papel: i === index ? 'origem_principal' : f.papel === 'origem_principal' ? 'referencia' : f.papel })),
    );
  };

  const removerFonte = (index: number) => {
    setFontesSel((prev) => prev.filter((_, i) => i !== index));
  };

  const adicionarFonte = () => {
    let novo: FonteSel | null = null;
    if (fonteAddModo === 'existente') {
      const cat = catalogo.find((c) => c.id === Number(fonteAddId));
      if (!cat) return;
      novo = { fonte_id: cat.id, fonte_nome: cat.nome, fonte_tipo: cat.tipo, papel: fonteAddPapel, url_especifica: fonteAddUrl.trim(), observacao: '' };
    } else {
      const nome = fonteAddNome.trim();
      if (!nome) return;
      novo = { fonte_id: null, fonte_nome: nome, fonte_tipo: fonteAddTipo, papel: fonteAddPapel, url_especifica: fonteAddUrl.trim(), observacao: '' };
    }
    if (!novo) return;

    setFontesSel((prev) => {
      const querPrincipal = novo!.papel === 'origem_principal';
      const temPrincipal = prev.some((f) => f.papel === 'origem_principal');
      const demoted = querPrincipal
        ? prev.map((f) => (f.papel === 'origem_principal' ? { ...f, papel: 'referencia' } : f))
        : prev;
      const papelFinal = querPrincipal ? 'origem_principal' : temPrincipal ? novo!.papel : 'origem_principal';
      return [...demoted, { ...novo!, papel: papelFinal }];
    });

    setFonteAddId('');
    setFonteAddNome('');
    setFonteAddUrl('');
    setFonteAddPapel('referencia');
    setFonteAddModo('existente');
    setFonteAddOpen(false);
  };

  const salvar = async () => {
    if (!titulo.trim()) {
      setError('Título é obrigatório.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const principal = fontesSel.find((f) => f.papel === 'origem_principal') ?? fontesSel[0];
      const { data, error: invokeErr } = await supabase.functions.invoke('radar-admin', {
        body: {
          action: 'salvar',
          password,
          oportunidade_id: op?.id ?? null,
          titulo: titulo.trim(),
          empresa,
          tipo: tipo || null,
          segmento,
          cidade,
          uf,
          regiao: regiao || null,
          data_descoberta: dt(dataDescoberta),
          estagio_projeto: estagioProjeto,
          previsao_execucao: dt(previsaoExecucao),
          previsao_compra: dt(previsaoCompra),
          valor_obra: num(valorObra),
          valor_contrato_cau: num(valorContratoCau),
          valor_potencial_hop: num(valorHop),
          custo_estimado: num(custo),
          lucro_potencial: num(lucro),
          margem_estimada: num(margem),
          solucoes,
          fonte: principal?.fonte_tipo || 'manual',
          url_fonte: principal?.url_especifica || '',
          confianca: confianca || null,
          responsavel,
          proxima_acao: proximaAcao,
          proxima_acao_em: dt(proximaAcaoEm),
          status,
          descricao,
          observacoes,
          fontes: fontesSel.map((f) => ({
            fonte_id: f.fonte_id,
            fonte_nome: f.fonte_nome,
            fonte_tipo: f.fonte_tipo,
            papel: f.papel,
            url_especifica: f.url_especifica,
            observacao: f.observacao,
          })),
        },
      });
      if (invokeErr) throw new Error(await extractErrorMessage(invokeErr));
      const parsed = data as { error?: string };
      if (parsed?.error) throw new Error(parsed.error);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erro ao salvar.');
    } finally {
      setSaving(false);
    }
  };

  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <div>
      <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2.5">{title}</h4>
      <div className="space-y-3">{children}</div>
    </div>
  );

  const fieldCls = 'w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:border-[#E8610A]';
  const labelCls = 'block text-[11px] font-semibold text-gray-500 uppercase tracking-wider mb-1';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl border border-gray-200 w-full max-w-3xl max-h-[92vh] flex flex-col shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between shrink-0">
          <div>
            <h3 className="font-bold text-gray-900 text-base">{op ? 'Editar oportunidade' : 'Nova oportunidade'}</h3>
            <p className="text-xs text-gray-500">Pode nascer com pouca informação e ser enriquecida depois.</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg cursor-pointer">
            <i className="ri-close-line"></i>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
          <Section title="Identificação">
            <div>
              <label className={labelCls}>Título *</label>
              <input type="text" value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ex: Nova sede da empresa X" className={fieldCls} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Empresa / órgão principal</label>
                <input type="text" value={empresa} onChange={(e) => setEmpresa(e.target.value)} placeholder="Nome da empresa" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Tipo</label>
                <select value={tipo} onChange={(e) => setTipo(e.target.value)} className={fieldCls}>
                  <option value="">Selecionar...</option>
                  {Object.entries(TIPO_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Segmento</label>
                <input type="text" value={segmento} onChange={(e) => setSegmento(e.target.value)} placeholder="Ex: Saúde, Indústria..." className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Cidade / UF</label>
                <div className="flex gap-2">
                  <input type="text" value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Cidade" className={fieldCls} />
                  <input type="text" value={uf} onChange={(e) => setUf(e.target.value)} placeholder="UF" className={`${fieldCls} w-20`} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Região</label>
                <select value={regiao} onChange={(e) => setRegiao(e.target.value)} className={fieldCls}>
                  <option value="">Selecionar...</option>
                  {REGIOES.map((r) => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Data da descoberta</label>
                <input type="date" value={dataDescoberta} onChange={(e) => setDataDescoberta(e.target.value)} className={fieldCls} />
              </div>
            </div>
          </Section>

          <Section title="Valores">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Valor da obra (R$)</label>
                <input type="number" value={valorObra} onChange={(e) => setValorObra(e.target.value)} placeholder="0" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Valor de contrato técnico (CAU/RRT)</label>
                <input type="number" value={valorContratoCau} onChange={(e) => setValorContratoCau(e.target.value)} placeholder="0" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Valor potencial HOP (R$)</label>
                <input type="number" value={valorHop} onChange={(e) => setValorHop(e.target.value)} placeholder="0" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Custo estimado (R$)</label>
                <input type="number" value={custo} onChange={(e) => setCusto(e.target.value)} placeholder="0" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Lucro potencial (R$)</label>
                <input type="number" value={lucro} onChange={(e) => setLucro(e.target.value)} placeholder="0" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Margem estimada (%)</label>
                <input type="number" value={margem} onChange={(e) => setMargem(e.target.value)} placeholder="Ex: 35" className={fieldCls} />
              </div>
            </div>
          </Section>

          <Section title="Timing">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className={labelCls}>Estágio do projeto</label>
                <input type="text" value={estagioProjeto} onChange={(e) => setEstagioProjeto(e.target.value)} placeholder="Ex: em projeto, em execução..." className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Previsão de execução</label>
                <input type="date" value={previsaoExecucao} onChange={(e) => setPrevisaoExecucao(e.target.value)} className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Previsão de compra/mobiliamento</label>
                <input type="date" value={previsaoCompra} onChange={(e) => setPrevisaoCompra(e.target.value)} className={fieldCls} />
              </div>
            </div>
          </Section>

          <Section title="Soluções HOP possíveis">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(SOLUCOES_LABELS).map(([k, v]) => {
                const ativa = solucoes.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => toggleSolucao(k)}
                    className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-3 py-1.5 border cursor-pointer whitespace-nowrap transition-colors ${
                      ativa ? 'bg-[#E8610A] text-white border-[#E8610A]' : 'bg-white text-gray-600 border-gray-200 hover:border-[#E8610A]/40'
                    }`}
                  >
                    {ativa && <i className="ri-check-line"></i>}
                    {v}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section title="Fontes">
            {fontesSel.length === 0 ? (
              <p className="text-sm text-gray-400">Nenhuma fonte vinculada ainda.</p>
            ) : (
              <div className="space-y-2">
                {fontesSel.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 border border-gray-100 rounded-xl px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-sm font-semibold text-gray-900">{f.fonte_nome || 'Nova fonte'}</span>
                        {f.fonte_tipo && (
                          <span className="text-[11px] font-semibold text-gray-500 bg-gray-100 rounded-full px-2 py-0.5 whitespace-nowrap">
                            {FONTE_TIPO_LABELS[f.fonte_tipo] ?? f.fonte_tipo}
                          </span>
                        )}
                        <span className={`text-[11px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap ${
                          f.papel === 'origem_principal' ? 'bg-[#E8610A]/10 text-[#E8610A]' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {PAPEL_FONTE_LABELS[f.papel] ?? f.papel}
                        </span>
                      </div>
                      {f.url_especifica && <p className="text-xs text-[#E8610A] truncate mt-0.5">{f.url_especifica}</p>}
                    </div>
                    <button
                      type="button"
                      onClick={() => marcarPrincipal(i)}
                      className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${
                        f.papel === 'origem_principal' ? 'text-[#E8610A] bg-[#E8610A]/10' : 'text-gray-300 hover:text-[#E8610A] hover:bg-[#E8610A]/10'
                      }`}
                      title="Marcar como origem principal"
                    >
                      <i className="ri-star-line"></i>
                    </button>
                    <button
                      type="button"
                      onClick={() => removerFonte(i)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg cursor-pointer"
                      title="Remover fonte"
                    >
                      <i className="ri-close-line"></i>
                    </button>
                  </div>
                ))}
              </div>
            )}

            {!fonteAddOpen ? (
              <button
                type="button"
                onClick={() => setFonteAddOpen(true)}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E8610A] hover:text-[#C4500A] cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line"></i>
                Adicionar fonte
              </button>
            ) : (
              <div className="border border-gray-200 rounded-xl p-3 space-y-3 bg-gray-50/50">
                <div className="inline-flex items-center bg-gray-100 rounded-full px-1 py-1">
                  <button
                    type="button"
                    onClick={() => setFonteAddModo('existente')}
                    className={`text-xs font-semibold rounded-full px-3 py-1 cursor-pointer whitespace-nowrap ${fonteAddModo === 'existente' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                  >
                    Fonte existente
                  </button>
                  <button
                    type="button"
                    onClick={() => setFonteAddModo('nova')}
                    className={`text-xs font-semibold rounded-full px-3 py-1 cursor-pointer whitespace-nowrap ${fonteAddModo === 'nova' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                  >
                    Nova fonte
                  </button>
                </div>

                {fonteAddModo === 'existente' ? (
                  <div>
                    <label className={labelCls}>Fonte</label>
                    <select value={fonteAddId} onChange={(e) => setFonteAddId(e.target.value ? Number(e.target.value) : '')} className={fieldCls}>
                      <option value="">Selecione uma fonte...</option>
                      {catalogo.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome} · {FONTE_TIPO_LABELS[c.tipo] ?? c.tipo}</option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className={labelCls}>Nome da fonte</label>
                      <input type="text" value={fonteAddNome} onChange={(e) => setFonteAddNome(e.target.value)} placeholder="Ex: Diário de Pernambuco" className={fieldCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Tipo</label>
                      <select value={fonteAddTipo} onChange={(e) => setFonteAddTipo(e.target.value)} className={fieldCls}>
                        {FONTE_TIPOS.map((t) => (
                          <option key={t.key} value={t.key}>{t.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>Papel</label>
                    <select value={fonteAddPapel} onChange={(e) => setFonteAddPapel(e.target.value)} className={fieldCls}>
                      {Object.entries(PAPEL_FONTE_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>URL específica (opcional)</label>
                    <input type="text" value={fonteAddUrl} onChange={(e) => setFonteAddUrl(e.target.value)} placeholder="https://..." className={fieldCls} />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setFonteAddOpen(false)}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-semibold text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={adicionarFonte}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                    Adicionar
                  </button>
                </div>
              </div>
            )}
          </Section>

          <Section title="Confiança">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Nível de confiança</label>
                <select value={confianca} onChange={(e) => setConfianca(e.target.value)} className={fieldCls}>
                  <option value="">Não definido</option>
                  {Object.entries(CONFIANCA_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>
            </div>
          </Section>

          <Section title="Comercial">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Responsável comercial</label>
                <input type="text" value={responsavel} onChange={(e) => setResponsavel(e.target.value)} placeholder="Nome" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Status</label>
                <select value={status} onChange={(e) => setStatus(e.target.value)} className={fieldCls}>
                  {STATUS_FLOW.map((k) => (
                    <option key={k} value={k}>{STATUS_LABELS[k] ?? k}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Próxima ação</label>
                <input type="text" value={proximaAcao} onChange={(e) => setProximaAcao(e.target.value)} placeholder="Ex: falar com o diretor" className={fieldCls} />
              </div>
              <div>
                <label className={labelCls}>Data da próxima ação</label>
                <input type="date" value={proximaAcaoEm} onChange={(e) => setProximaAcaoEm(e.target.value)} className={fieldCls} />
              </div>
            </div>
          </Section>

          <Section title="Detalhes">
            <div>
              <label className={labelCls}>Descrição</label>
              <textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} rows={3} placeholder="O que está acontecendo..." className={`${fieldCls} resize-y`} />
            </div>
            <div>
              <label className={labelCls}>Observações</label>
              <textarea value={observacoes} onChange={(e) => setObservacoes(e.target.value)} rows={2} placeholder="Anotações internas..." className={`${fieldCls} resize-y`} />
            </div>
          </Section>

          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>}
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-2 shrink-0 bg-white">
          <button onClick={onClose} className="inline-flex items-center px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer whitespace-nowrap">
            Cancelar
          </button>
          <button
            onClick={salvar}
            disabled={saving}
            className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-semibold text-white bg-[#E8610A] hover:bg-[#C4500A] rounded-lg cursor-pointer whitespace-nowrap disabled:opacity-60"
          >
            {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
            Salvar
          </button>
        </div>
      </div>
    </div>
  );
}